import { Controller } from "@hotwired/stimulus"

// Debounced background autosave for singleton forms. Posts the whole form to its
// existing action via fetch, so the user keeps editing uninterrupted. Singletons
// overwrite one Response per practice, so every save IS the live record — there
// is no separate draft. The server answers an X-Autosave request with 204, which
// never navigates the page. Mounted on the <form> element.
export default class extends Controller {
  static targets = ["status"]
  static values = { debounce: { type: Number, default: 5000 } }

  connect() {
    this.dirty = false
    this.saving = false
    this.timer = null
    this.flush = this.flush.bind(this)
    // Flush a pending change if the page goes away before the debounce fires —
    // a Turbo visit (incl. native push nav) or the tab/app closing.
    window.addEventListener("pagehide", this.flush)
    document.addEventListener("turbo:before-visit", this.flush)
  }

  disconnect() {
    this.flush()
    clearTimeout(this.timer)
    window.removeEventListener("pagehide", this.flush)
    document.removeEventListener("turbo:before-visit", this.flush)
  }

  // Bound to input + change on the form. Resets the debounce window. Gives
  // immediate feedback so there's no silent gap during the debounce wait.
  schedule() {
    this.dirty = true
    this.setStatus("Unsaved changes…")
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), this.debounceValue)
  }

  // Fire any pending save immediately (teardown/navigation).
  flush() {
    if (this.dirty && !this.saving) {
      clearTimeout(this.timer)
      this.save()
    }
  }

  async save() {
    if (this.saving) return // serialize; re-checks dirty on completion
    this.saving = true
    this.dirty = false
    this.setStatus("Saving…")

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.element.action, {
        method: "POST", // _method=patch (edit mode) is handled by Rails
        headers: { "X-Autosave": "1", "X-CSRF-Token": token, "Accept": "application/json" },
        body: new FormData(this.element),
        keepalive: true, // survive an unload while flushing
      })
      if (res.ok) {
        this.setStatus(`Saved ${this.now()}`, "text-green-600")
      } else {
        this.dirty = true // leave dirty so the next change retries
        this.setStatus("Couldn't save — changes kept", "text-amber-600")
      }
    } catch {
      this.dirty = true
      this.setStatus("Couldn't save — changes kept", "text-amber-600")
    } finally {
      this.saving = false
      // A change that landed mid-save reschedules itself.
      if (this.dirty) this.schedule()
    }
  }

  // Swaps the status text and its color tone, keeping the base size class.
  setStatus(text, color = "text-gray-500") {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.remove("text-gray-500", "text-green-600", "text-amber-600")
    this.statusTarget.classList.add(color)
  }

  now() {
    return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
  }
}
